import { useMutation } from "@tanstack/react-query"

import { Job, VariableValue, VariableValues } from "@easy-templates/types"
import { useAppContext } from "@easy-templates/ui"
import { CreateIssuesData } from "@easy-templates/lib"

interface Return {
  isSubmitting: boolean
  createIssuesFromTemplate: (params: {
    templateId: string
    projectId: string
    variableValues: VariableValues
    issueIds: string[]
    rootIssueKey?: string
  }) => unknown
}

type Result = { jobId?: string } | Job

type Props = {
  onError: (error: Error) => void
  onSuccess: (result: Result) => void
}

type MutationParams = {
  templateId: string
  projectId: string
  variableValues: VariableValues
  issueIds: string[]
  rootIssueKey?: string
}

const normalizeVariableValue = (value: string | number | { value: string }): VariableValue => {
  if (!value && value !== 0) {
    return null
  }

  if (typeof value === 'object' && 'value' in value) {
    return value.value
  }

  if (typeof value === 'number') {
    return String(value)
  }

  return value
}
export default ({ onSuccess, onError }: Props): Return => {
  const { core } = useAppContext()
  const { mutate: createIssuesFromTemplate, isPending: isSubmitting } =
    useMutation<Result, Error, MutationParams>({
      mutationFn: async ({ templateId, projectId, variableValues: givenVariableValues, rootIssueKey, issueIds }) => {
        const variableValues: VariableValues = Object.fromEntries(
          Object.entries(givenVariableValues)
            .map(([label, value]) => ([label, normalizeVariableValue(value)]))
        )

        const data = new CreateIssuesData({
          templateId,
          projectId,
          variableValues,
          issueIds,
          rootIssueKey,
        })

        const result = await core.createFromTemplate(data)

        return result
      },
      onSuccess,
      onError,
    })

  return {
    isSubmitting,
    createIssuesFromTemplate,
  }
}
